import React, { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, MenuItem, Switch, FormControlLabel, CircularProgress } from '@material-ui/core'
import { DropzoneDialog } from 'material-ui-dropzone'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { staffEasebuzzCreateThunk, staffEasebuzzUpdateThunk } from 'store/reducers/temple/easebuzzSubmerchantSlice'
import { useSnackbar } from 'notistack'
import { unwrapResult } from '@reduxjs/toolkit'
import { selectTempleById } from 'store/reducers/temple/templeSlice'
import { selectAllBankAccounts, readBankAccount } from 'store/reducers/temple/bankAccountSlice'
import { fileUploadApi } from 'api/temple'
import { EasebuzzSubmerchantType } from 'types/api/temple'

export type FormValues = {
    subMerchantName?: string
    subMerchantEmail?: string
    subMerchantPhone?: string
    bankAccount?: string
    kycBankProof?: string
    kycIdProof?: string
    kycExtraDoc?: string
    dcDeductionPercentageLtOneThousand?: number | ''
    dcDeductionPercentageLtTwoThousand?: number | ''
    dcDeductionPercentageGtTwoThousand?: number | ''
    nbDeduction?: number | ''
    isNbRsPercentage?: boolean
    upiDeductionPercentageLtLimit?: number | ''
    upiDeductionPercentageGtLimit?: number | ''
    upiLimit?: number | ''
    isUpiRsPercentage?: boolean
    cashcardDeductionPercentage?: number | ''
    debitatmDeductionPercentage?: number | ''
    otherDeductionPercentage?: number | ''
    subMerchantGstin?: string
    subMerchantBusinessName?: string
    subMerchantBusinessAddress?: string
    subMerchantState?: string
    subMerchantBusinessNature?: string
    temple?: string | number
}

const schema = Yup.object().shape({
    subMerchantName: Yup.string().max(128, 'Max 128 chars'),
    subMerchantEmail: Yup.string().email('Invalid email').nullable().notRequired(),
    subMerchantGstin: Yup.string().max(15, 'Max 15 chars')
})

type Props = {
    open: boolean
    onClose: () => void
    templeId?: string | number | null
    bankAccountUrl?: string | null
    initialData?: Partial<EasebuzzSubmerchantType> | undefined
    onSaved?: () => void
}

const EasebuzzSubmerchantForm: React.FC<Props> = ({ open, onClose, templeId, bankAccountUrl, initialData, onSaved }) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const temple = useAppSelector(state => selectTempleById(state, templeId ?? ''))

    const bankAccounts = useAppSelector(selectAllBankAccounts)
    const templeBankAccounts = useMemo(() => bankAccounts.filter(ba => ba.temple === temple?.url), [bankAccounts, temple?.url])

    useEffect(() => {
        if (temple?.id && templeBankAccounts.length === 0) {
            dispatch(readBankAccount({ id: temple.id, url: temple?.url ?? '' }))
        }
    }, [temple?.id, templeBankAccounts.length, dispatch])

    const initialValues: FormValues = {
        subMerchantName: initialData?.subMerchantName ?? '',
        subMerchantEmail: initialData?.subMerchantEmail ?? '',
        subMerchantPhone: initialData?.subMerchantPhone ?? '',
        bankAccount: initialData?.bankAccount ?? bankAccountUrl ?? '',
        kycBankProof: initialData?.kycBankProof ?? '',
        kycIdProof: initialData?.kycIdProof ?? '',
        kycExtraDoc: initialData?.kycExtraDoc ?? '',
        dcDeductionPercentageLtOneThousand: initialData?.dcDeductionPercentageLtOneThousand ?? '',
        dcDeductionPercentageLtTwoThousand: initialData?.dcDeductionPercentageLtTwoThousand ?? '',
        dcDeductionPercentageGtTwoThousand: initialData?.dcDeductionPercentageGtTwoThousand ?? '',
        nbDeduction: initialData?.nbDeduction ?? '',
        isNbRsPercentage: initialData?.isNbRsPercentage ?? false,
        upiDeductionPercentageLtLimit: initialData?.upiDeductionPercentageLtLimit ?? '',
        upiDeductionPercentageGtLimit: initialData?.upiDeductionPercentageGtLimit ?? '',
        upiLimit: initialData?.upiLimit ?? '',
        isUpiRsPercentage: initialData?.isUpiRsPercentage ?? false,
        cashcardDeductionPercentage: initialData?.cashcardDeductionPercentage ?? '',
        debitatmDeductionPercentage: initialData?.debitatmDeductionPercentage ?? '',
        otherDeductionPercentage: initialData?.otherDeductionPercentage ?? '',
        subMerchantGstin: initialData?.subMerchantGstin ?? '',
        subMerchantBusinessName: initialData?.subMerchantBusinessName ?? '',
        subMerchantBusinessAddress: initialData?.subMerchantBusinessAddress ?? '',
        subMerchantState: initialData?.subMerchantState ?? '',
        subMerchantBusinessNature: initialData?.subMerchantBusinessNature ?? '',
    }

    const [bankProofDialogOpen, setBankProofDialogOpen] = useState(false)
    const [idProofDialogOpen, setIdProofDialogOpen] = useState(false)
    const [extraDocDialogOpen, setExtraDocDialogOpen] = useState(false)
    const [uploading, setUploading] = useState(false)

    const handleSubmit = async (values: FormValues, helpers: any) => {
        try {
            // convert camelCase form values to snake_case API expected keys
            const apiPayload: any = {
                temple: temple?.url,
                bank_account: values.bankAccount || undefined,
                sub_merchant_name: values.subMerchantName || undefined,
                sub_merchant_email: values.subMerchantEmail || undefined,
                sub_merchant_phone: values.subMerchantPhone || undefined,
                kyc_bank_proof: values.kycBankProof || undefined,
                kyc_id_proof: values.kycIdProof || undefined,
                kyc_extra_doc: values.kycExtraDoc || undefined,
                dc_deduction_percentage_lt_one_thousand: values.dcDeductionPercentageLtOneThousand === '' ? undefined : Number(values.dcDeductionPercentageLtOneThousand),
                dc_deduction_percentage_lt_two_thousand: values.dcDeductionPercentageLtTwoThousand === '' ? undefined : Number(values.dcDeductionPercentageLtTwoThousand),
                dc_deduction_percentage_gt_two_thousand: values.dcDeductionPercentageGtTwoThousand === '' ? undefined : Number(values.dcDeductionPercentageGtTwoThousand),
                nb_deduction: values.nbDeduction === '' ? undefined : Number(values.nbDeduction),
                is_nb_rs_percentage: !!values.isNbRsPercentage,
                upi_deduction_percentage_lt_limit: values.upiDeductionPercentageLtLimit === '' ? undefined : Number(values.upiDeductionPercentageLtLimit),
                upi_deduction_percentage_gt_limit: values.upiDeductionPercentageGtLimit === '' ? undefined : Number(values.upiDeductionPercentageGtLimit),
                upi_limit: values.upiLimit === '' ? undefined : Number(values.upiLimit),
                is_upi_rs_percentage: !!values.isUpiRsPercentage,
                cashcard_deduction_percentage: values.cashcardDeductionPercentage === '' ? undefined : Number(values.cashcardDeductionPercentage),
                debitatm_deduction_percentage: values.debitatmDeductionPercentage === '' ? undefined : Number(values.debitatmDeductionPercentage),
                other_deduction_percentage: values.otherDeductionPercentage === '' ? undefined : Number(values.otherDeductionPercentage),
                sub_merchant_gstin: values.subMerchantGstin || undefined,
                sub_merchant_business_name: values.subMerchantBusinessName || undefined,
                sub_merchant_business_address: values.subMerchantBusinessAddress || undefined,
                sub_merchant_state: values.subMerchantState || undefined,
                sub_merchant_business_nature: values.subMerchantBusinessNature || undefined,
            }

            if (initialData?.url) {
                await dispatch(staffEasebuzzUpdateThunk({ url: initialData.url as string, data: apiPayload })).then(unwrapResult)
                enqueueSnackbar('Submerchant updated', { variant: 'success' })
            } else {
                await dispatch(staffEasebuzzCreateThunk(apiPayload)).then(unwrapResult)
                enqueueSnackbar('Submerchant created', { variant: 'success' })
            }

            helpers.setSubmitting(false)
            onClose()
            if (onSaved) onSaved()
        } catch (err: any) {
            enqueueSnackbar('Error saving submerchant', { variant: 'error' })
            helpers.setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{initialData?.url ? 'Edit Easebuzz Submerchant' : 'Create Easebuzz Submerchant'}</DialogTitle>
            <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit} enableReinitialize>
                {({ isSubmitting, errors, touched, handleChange, values, setFieldValue }) => (
                    <Form>
                        <DialogContent>
                            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                                <TextField
                                    label="Sub-merchant name"
                                    name="subMerchantName"
                                    value={values.subMerchantName}
                                    onChange={handleChange}
                                    error={!!(errors.subMerchantName && touched.subMerchantName)}
                                    helperText={touched.subMerchantName && errors.subMerchantName}
                                    fullWidth
                                />
                                <TextField
                                    label="Email"
                                    name="subMerchantEmail"
                                    value={values.subMerchantEmail}
                                    onChange={handleChange}
                                    error={!!(errors.subMerchantEmail && touched.subMerchantEmail)}
                                    helperText={touched.subMerchantEmail && errors.subMerchantEmail}
                                    fullWidth
                                />
                                <TextField
                                    label="Phone"
                                    name="subMerchantPhone"
                                    value={values.subMerchantPhone}
                                    onChange={handleChange}
                                    error={!!(errors.subMerchantPhone && touched.subMerchantPhone)}
                                    helperText={touched.subMerchantPhone && errors.subMerchantPhone}
                                    fullWidth
                                />

                                <TextField
                                    label="Bank account"
                                    name="bankAccount"
                                    value={values.bankAccount}
                                    onChange={handleChange}
                                    select
                                    fullWidth
                                    disabled={templeBankAccounts.length === 0}
                                >
                                    {templeBankAccounts.length === 0 ? (
                                        <MenuItem value="" disabled>No bank accounts</MenuItem>
                                    ) : (
                                        templeBankAccounts.map(ba => (
                                            <MenuItem key={ba.url} value={ba.url}>
                                                {ba.bankName ? `${ba.bankName} — ${ba.accNumber}` : ba.accNumber}
                                            </MenuItem>
                                        ))
                                    )}
                                </TextField>

                                {/* KYC file uploads */}
                                <Box display="flex" style={{ gap: 12, alignItems: 'center' }}>
                                    <Box flex={1}>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                            <Button variant="outlined" size="small" onClick={() => setBankProofDialogOpen(true)}>Upload bank proof</Button>
                                            {values.kycBankProof ? <a href={values.kycBankProof} target="_blank" rel="noreferrer">View</a> : <em>No file</em>}
                                            {uploading && <CircularProgress size={16} />}
                                        </Box>
                                    </Box>

                                    <Box flex={1}>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                            <Button variant="outlined" size="small" onClick={() => setIdProofDialogOpen(true)}>Upload ID proof</Button>
                                            {values.kycIdProof ? <a href={values.kycIdProof} target="_blank" rel="noreferrer">View</a> : <em>No file</em>}
                                            {uploading && <CircularProgress size={16} />}
                                        </Box>
                                    </Box>

                                    <Box flex={1}>
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                            <Button variant="outlined" size="small" onClick={() => setExtraDocDialogOpen(true)}>Upload extra doc</Button>
                                            {values.kycExtraDoc ? <a href={values.kycExtraDoc} target="_blank" rel="noreferrer">View</a> : <em>No file</em>}
                                            {uploading && <CircularProgress size={16} />}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Deduction/charges fields - decimals */}
                                <Box display="flex" style={{ gap: 12 }}>
                                    <TextField label="DC % (<1000)" name="dcDeductionPercentageLtOneThousand" value={values.dcDeductionPercentageLtOneThousand} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <TextField label="DC % (<2000)" name="dcDeductionPercentageLtTwoThousand" value={values.dcDeductionPercentageLtTwoThousand} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <TextField label="DC % (>2000)" name="dcDeductionPercentageGtTwoThousand" value={values.dcDeductionPercentageGtTwoThousand} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                </Box>

                                <Box display="flex" style={{ gap: 12 }}>
                                    <TextField label="NB deduction %" name="nbDeduction" value={values.nbDeduction} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <FormControlLabel control={<Switch checked={!!values.isNbRsPercentage} onChange={(e) => setFieldValue('isNbRsPercentage', e.target.checked)} />} label="NB charges in Rs?" />
                                </Box>

                                <Box display="flex" style={{ gap: 12 }}>
                                    <TextField label="UPI % (lt limit)" name="upiDeductionPercentageLtLimit" value={values.upiDeductionPercentageLtLimit} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <TextField label="UPI % (gt limit)" name="upiDeductionPercentageGtLimit" value={values.upiDeductionPercentageGtLimit} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <TextField label="UPI limit" name="upiLimit" value={values.upiLimit} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <FormControlLabel control={<Switch checked={!!values.isUpiRsPercentage} onChange={(e) => setFieldValue('isUpiRsPercentage', e.target.checked)} />} label="UPI charges in Rs?" />
                                </Box>

                                <Box display="flex" style={{ gap: 12 }}>
                                    <TextField label="Cashcard %" name="cashcardDeductionPercentage" value={values.cashcardDeductionPercentage} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <TextField label="DebitATM %" name="debitatmDeductionPercentage" value={values.debitatmDeductionPercentage} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                    <TextField label="Other %" name="otherDeductionPercentage" value={values.otherDeductionPercentage} onChange={handleChange} type="number" inputProps={{ step: '0.01' }} />
                                </Box>

                                <TextField label="GSTIN" name="subMerchantGstin" value={values.subMerchantGstin} onChange={handleChange} fullWidth />
                                <TextField label="Business name" name="subMerchantBusinessName" value={values.subMerchantBusinessName} onChange={handleChange} fullWidth />
                                <TextField label="Business address" name="subMerchantBusinessAddress" value={values.subMerchantBusinessAddress} onChange={handleChange} fullWidth multiline rows={3} />
                                <Box display="flex" style={{ gap: 12 }}>
                                    <TextField label="State" name="subMerchantState" value={values.subMerchantState} onChange={handleChange} />
                                    <TextField label="Business nature" name="subMerchantBusinessNature" value={values.subMerchantBusinessNature} onChange={handleChange} />
                                </Box>

                                {/* DropzoneDialogs - keep them inside Formik render to have setFieldValue */}
                                <DropzoneDialog
                                    open={bankProofDialogOpen}
                                    onSave={async (files: File[]) => {
                                        if (files && files.length > 0) {
                                            try {
                                                setUploading(true)
                                                const resp = await fileUploadApi.create(files[0], templeId ?? temple?.id ?? '')
                                                const url = resp.data?.url
                                                if (url) setFieldValue('kycBankProof', url)
                                            } finally {
                                                setUploading(false)
                                                setBankProofDialogOpen(false)
                                            }
                                        } else {
                                            setBankProofDialogOpen(false)
                                        }
                                    }}
                                    onClose={() => setBankProofDialogOpen(false)}
                                    filesLimit={1}
                                    showPreviewsInDropzone={true}
                                />

                                <DropzoneDialog
                                    open={idProofDialogOpen}
                                    onSave={async (files: File[]) => {
                                        if (files && files.length > 0) {
                                            try {
                                                setUploading(true)
                                                const resp = await fileUploadApi.create(files[0], templeId ?? temple?.id ?? '')
                                                const url = resp.data?.url
                                                if (url) setFieldValue('kycIdProof', url)
                                            } finally {
                                                setUploading(false)
                                                setIdProofDialogOpen(false)
                                            }
                                        } else setIdProofDialogOpen(false)
                                    }}
                                    onClose={() => setIdProofDialogOpen(false)}
                                    filesLimit={1}
                                    showPreviewsInDropzone={true}
                                />

                                <DropzoneDialog
                                    open={extraDocDialogOpen}
                                    onSave={async (files: File[]) => {
                                        if (files && files.length > 0) {
                                            try {
                                                setUploading(true)
                                                const resp = await fileUploadApi.create(files[0], templeId ?? temple?.id ?? '')
                                                const url = resp.data?.url
                                                if (url) setFieldValue('kycExtraDoc', url)
                                            } finally {
                                                setUploading(false)
                                                setExtraDocDialogOpen(false)
                                            }
                                        } else setExtraDocDialogOpen(false)
                                    }}
                                    onClose={() => setExtraDocDialogOpen(false)}
                                    filesLimit={1}
                                    showPreviewsInDropzone={true}
                                />

                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="default">Cancel</Button>
                            <Button type="submit" color="primary" disabled={isSubmitting || uploading}>
                                {initialData?.url ? (isSubmitting ? <CircularProgress size={18} /> : 'Save') : (isSubmitting ? <CircularProgress size={18} /> : 'Create')}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    )
}

export default EasebuzzSubmerchantForm
